import { debounce } from './utils';

/**
 * Enqueue a matomo command.
 * @param {array} command The command to execute.
 */
const matomoCommand = (command = []) => {
    window._paq = window._paq || [];
    window._paq.push(command);
};

/**
 * Track an event in Matomo.
 * @param {string} category Event category.
 * @param {string} action Event action.
 * @param {string} name Event name.
 * @param {int} value Event value (optional).
 */
const trackEvent = (category, action, name, value = null) => {
    const eventData = ['trackEvent', category, action, name];
    if (value) eventData.push(value);
    matomoCommand(eventData);
};

/**
 * Track a search query in Matomo.
 * @param {string} query The search query
 */
const trackSearchQuery = query => {
    matomoCommand(['trackSiteSearch', query, false, false]);
};

// filter newState to include only facet filters that were added (not removed!) between the previous and new state
const getChangedFilters = (previousState, newState) => {
    const previousFacets = {
        ...(previousState.menu || []),
        ...(previousState.refinementList || []),
    };
    const newFacets = {
        ...(newState.menu || []),
        ...(newState.refinementList || []),
    };

    const getNewFacetValues = (prevValues, newValues) => {
        if (Array.isArray(newValues)) {
            return newValues.filter(value => !(prevValues || []).includes(value));
        }
        if (typeof newValues === 'string') {
            return prevValues !== newValues ? [newValues] : null;
        }
    };
    return Object.fromEntries(
        Object.entries(newFacets)
            .map(([name, values = []]) => [name, getNewFacetValues(previousFacets[name], values)])
            .filter(([_, values]) => values && values.length),
    );
};

export const eventTrackingMiddlewareFactory =
    (indexName, hitsWidgetContainer) =>
    ({ instantSearchInstance }) => {
        let previousState = {};
        const trackQueryDebounced = debounce(query => trackSearchQuery(query), 1000);

        return {
            onStateChange({ uiState }) {
                const state = uiState[indexName];
                if (!state) return;

                // track updated query terms (unless the previous query was)
                const hasNewQuery =
                    state.query &&
                    previousState.query != state.query &&
                    !(previousState.query || '').includes(state.query);
                if (hasNewQuery) trackQueryDebounced(state.query);

                // track filter / facet usage
                const changedFilters = getChangedFilters(previousState, state);
                Object.entries(changedFilters).forEach(([name, values]) => {
                    trackEvent('search', 'filter_used', name);
                    values.forEach(value => trackEvent('search', 'facet_added', value));
                });

                // track newly added geohash filters
                const configuredFilters = state.configure?.filters;
                const previousConfiguredFilters = previousState.configure?.filters;
                if (configuredFilters && configuredFilters !== previousConfiguredFilters) {
                    const geohashRegex = new RegExp(/geohash_\d = ([a-z0-9]{2,6})/);
                    const result = configuredFilters.match(geohashRegex);
                    const geohash = result ? result[1] : null;
                    if (geohash) trackEvent('search', 'geohash_used', geohash);
                }

                previousState = state;
            },
            subscribe() {
                // on startup, set the previous state to the initial state (including any restored query parameters)
                previousState = instantSearchInstance._initialUiState[indexName] || {};

                // listen to clicks inside the search results to track clicks on results
                const resultsContainer = document.querySelector(hitsWidgetContainer);
                resultsContainer.addEventListener('click', e => {
                    const target = e.target;
                    if (target.nodeName !== 'A') return;
                    const hitTitle = e.target.textContent;
                    if (!hitTitle) return;
                    trackEvent('search', 'result_clicked', hitTitle);
                });
            },
            unsubscribe() {},
        };
    };
